import { useCallback } from 'react';
import { getSignedDownloadUrl } from '@medflex/utils/handleFile';
import config from '@medflex/utils/config';
import { trackCustomEvent } from '@medflex/utils/Tracking';
import Logger from '@medflex/utils/Logger';
import { showSuccessToast, showAlertToast } from '@medflex/components/stories/Toast';
import { useTranslation } from 'react-i18next';
import useUserSession from '@medflex/hooks/useUserSession';
import isDesktop from '@medflex/utils/isDesktop';
import { gdtConstants, gdtHelpers } from '@medflex/gdt';

const { IpcResponses, PvsSelectOptions, FileSources } = gdtConstants;

const FILE_SOURCE_URLS = {
  PAM: config.PAM_API_URL,
  CONTACT: config.INSTANT_MESSAGE_SERVICE_ENDPOINT,
  SESSION: `${config.INSTANT_MESSAGE_SERVICE_ENDPOINT}/sessions`,
};

const useGdt = pvsId => {
  const { t } = useTranslation('physician', { keyPrefix: 'gdt' });
  const { session: userSession } = useUserSession();

  const canWriteExamination = isDesktop() && pvsId;

  const canOpenInPVS =
    isDesktop() && pvsId && gdtHelpers.selectedPVS.get() === PvsSelectOptions.MEDICAL_OFFICE;

  const handleWriteExaminationResponse = useCallback(
    async examination => {
      const res = await gdtHelpers.writeExamination.write(examination);

      if (res === IpcResponses.SUCCESS) {
        showSuccessToast(
          t('writeExamination.success.title'),
          t('writeExamination.success.description'),
          {
            toastId: 'writeExaminationSuccess',
          },
        );
        trackCustomEvent({
          eventCategory: 'pvs_integration',
          eventAction: 'write_examination',
          eventName: 'write_examination_messenger',
        });
      }
    },
    [t],
  );

  const onWriteExamination = useCallback(
    async ({ text, files, fileSource = FileSources.SESSION }) => {
      try {
        const examination = {
          pvsId,
          ...(text && {
            text,
          }),
        };

        if (!files?.length) {
          return handleWriteExaminationResponse(examination);
        }

        const fileData = await Promise.all(
          files.map(async ({ id, name, mediaType }) => {
            const url = await getSignedDownloadUrl({
              uri: `${FILE_SOURCE_URLS[fileSource]}/files/${id}`,
              token: userSession.access_token,
            });

            return { url, name, mediaType };
          }),
        );

        return handleWriteExaminationResponse({ files: fileData, ...examination });
      } catch (e) {
        Logger.error('[onWriteExamination]:  Error while writing examination from messenger', e);
        return showAlertToast(t('writeExamination.error'), null, {
          toastId: 'writeExaminationError',
        });
      }
    },
    [pvsId, handleWriteExaminationResponse, userSession.access_token, t],
  );

  const handleOpenInPVS = useCallback(async () => {
    try {
      const res = await gdtHelpers.openPatientInPVS.open(pvsId);

      if (res === IpcResponses.SUCCESS) {
        showSuccessToast(
          t('openPatientInPVS.success.title'),
          t('openPatientInPVS.success.description'),
          {
            toastId: 'openPatientInPVSSuccess',
          },
        );
        trackCustomEvent({
          eventCategory: 'pvs_integration',
          eventAction: 'open_patient_in_pvs',
          eventName: 'open_patient_in_pvs_messenger',
        });
      }
    } catch (e) {
      showAlertToast(t('openPatientInPVS.error'), null, {
        toastId: 'openPatientInPVSError',
      });
      Logger.error('[handleOpenInPVS]:  Error while opening patient in PVS from messenger', e);
    }
  }, [pvsId, t]);

  return { onWriteExamination, canWriteExamination, canOpenInPVS, handleOpenInPVS };
};

export default useGdt;
